export * from '@egovsolutions/angular-egov-api/cart/common';
import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { isEcheckPaymentData, isUSCardPaymentData } from '@egovsolutions/angular-egov-api/cart/internal';
import * as i1 from '@egovsolutions/angular-egov-api/common';
import { createEgovAPIError } from '@egovsolutions/angular-egov-api/common';
import { adaptPPTEntities } from '@egovsolutions/angular-egov-api/ppt/internal/basic-models';
import { isString } from '@egovsolutions/type-checking';
import { map } from 'rxjs/operators';

/* eslint-disable @typescript-eslint/naming-convention */
class CartNetworkService {
    constructor(api) {
        this.api = api;
    }
    // Cart
    cart$() {
        return this.api.request$({ endpoint: 'cart/view/ALL?includeAllPayableBills=true' }).pipe(map(this.handleUnexpectedResponse), map(this.adaptPPTData));
    }
    // Add to cart
    addToCart$(items) {
        return this.api.request$({
            endpoint: 'cart/add?includeAllPayableBills=true',
            method: 'POST',
            bodyParameters: { cart_contents: JSON.stringify({ items }) }
        })
            .pipe(map(this.handleUnexpectedResponse), map(this.adaptPPTData));
    }
    // Remove from cart
    removeFromCart$(items) {
        return this.api.request$({
            endpoint: 'cart/remove?includeAllPayableBills=true',
            method: 'POST',
            bodyParameters: { cart_contents: JSON.stringify({ items }) }
        })
            .pipe(map(this.handleUnexpectedResponse), map(this.adaptPPTData));
    }
    // Update partial amount
    updatePartialAmount$(item, newPartialAmount) {
        return this.api.request$({
            endpoint: 'cart/update?includeAllPayableBills=true',
            method: 'POST',
            bodyParameters: { cart_contents: JSON.stringify({ items: [{ ...item, partialAmount: newPartialAmount }] }) }
        })
            .pipe(map(this.handleUnexpectedResponse), map(this.adaptPPTData));
    }
    // Empty cart
    emptyCart$() {
        return this.api.request$({
            endpoint: 'cart/remove/{{moduleId}}?includeAllPayableBills=true',
            urlFields: { moduleId: 'ALL' },
            method: 'POST',
        })
            .pipe(map(this.handleUnexpectedResponse), map(this.adaptPPTData));
    }
    // Submit payment
    submitCartPayment$(paymentData, grandTotalWithoutFees, grandTotalWithFees) {
        return this.api.request$({
            endpoint: 'cart/submit?includeAllPayableBills=true',
            method: 'POST',
            bodyParameters: {
                cart_contents: JSON.stringify(this.adaptPaymentDataToEndpoint(paymentData, grandTotalWithoutFees, grandTotalWithFees))
            }
        });
    }
    // Adapters
    adaptPaymentDataToEndpoint(paymentData, grandTotalWithoutFees, grandTotalWithFees) {
        const result = { total_payment: grandTotalWithFees };
        let data = { amount: grandTotalWithoutFees };
        if (!isString(paymentData)) {
            data = {
                ...data,
                name: paymentData.name,
                contact_email: paymentData.email,
                phone_number: paymentData.phone,
                address: paymentData.address1,
            };
            if (isEcheckPaymentData(paymentData)) {
                data = {
                    ...data,
                    checking_account_number: paymentData.accountNumber,
                    routing_number: paymentData.routingNumber,
                    zip: paymentData.zip
                };
                result.payments = { ECHECK: [data] };
            }
            else {
                data = {
                    ...data,
                    card_number: paymentData.cardNumber.split(' ').join(''),
                    cvv: paymentData.cv2,
                    expiration_month: paymentData.expirationMonth,
                    expiration_year: paymentData.expirationYear
                };
                if (isUSCardPaymentData(paymentData)) {
                    data.zip = paymentData.zip;
                    result.payments = { CREDIT: [data] };
                }
                else
                    result.payments = { CREDIT_INTL: [data] };
            }
        }
        else {
            Object.assign(data, { paymentMethod: paymentData });
            result.payments = { EXISTING: [data] };
        }
        return result;
    }
    adaptPPTData(actualCartResponse) {
        if (actualCartResponse?.cart_contents?.PPT) {
            const bills = actualCartResponse.cart_contents.PPT.bills || [];
            const parcels = actualCartResponse.cart_contents.PPT.parcels || [];
            const accounts = actualCartResponse.cart_contents.PPT.accounts || [];
            const adaptedBillsParcelsAndAccounts = adaptPPTEntities(accounts, parcels, bills, false);
            return {
                ...actualCartResponse,
                cart_contents: {
                    ...actualCartResponse.cart_contents,
                    PPT: {
                        ...actualCartResponse.cart_contents.PPT,
                        bills: adaptedBillsParcelsAndAccounts.bills,
                        parcels: adaptedBillsParcelsAndAccounts.parcels,
                        accounts: adaptedBillsParcelsAndAccounts.accounts,
                    }
                }
            };
        }
        else
            return actualCartResponse;
    }
    handleUnexpectedResponse(response) {
        if (!response || !Object.prototype.hasOwnProperty.call(response, 'cart_contents'))
            throw createEgovAPIError('Unexpected response from Server');
        return response;
    }
}
CartNetworkService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CartNetworkService, deps: [{ token: i1.EgovAPI }], target: i0.ɵɵFactoryTarget.Injectable });
CartNetworkService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CartNetworkService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CartNetworkService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.EgovAPI }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { CartNetworkService };
